import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";

export default function SettingsAdmin() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  const [form, setForm] = useState(null);

  useEffect(() => { if (data && !form) setForm(data); }, [data, form]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (obj, k, v) => setForm((f) => ({ ...f, [obj]: { ...(f[obj] || {}), [k]: v } }));

  const save = useMutation({
    mutationFn: async () => api.put("/admin/settings", form),
    onSuccess: () => { toast.success("Settings saved"); qc.invalidateQueries({ queryKey: ["admin-settings"] }); },
  });

  if (!form) return <p className="text-subtle">Loading...</p>;

  const metrics = form.trust_metrics || [];
  const social = form.social || {};
  const seo = form.seo || {};
  const analytics = form.analytics || {};

  return (
    <div className="max-w-3xl">
      <h1 className="font-heading text-2xl font-bold text-ink">Website Settings</h1>
      <p className="mt-1 text-sm text-subtle">Company details, disclosures, trust metrics, SEO and analytics IDs.</p>

      <Section title="Company Details">
        <F label="Company Name"><Input value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} /></F>
        <F label="Tagline"><Input value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} /></F>
        <F label="General Email (info)"><Input value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="settings-email" /></F>
        <F label="Support Email"><Input value={form.support_email || ""} onChange={(e) => set("support_email", e.target.value)} data-testid="settings-support-email" /></F>
        <F label="Marketing Email"><Input value={form.marketing_email || ""} onChange={(e) => set("marketing_email", e.target.value)} data-testid="settings-marketing-email" /></F>
        <F label="Legal Email"><Input value={form.legal_email || ""} onChange={(e) => set("legal_email", e.target.value)} data-testid="settings-legal-email" /></F>
        <F label="Phone"><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></F>
        <F label="WhatsApp (with country code)"><Input value={form.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} data-testid="settings-whatsapp" placeholder="+961 79 09 42 95" /></F>
        <F label="Address" full><Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} /></F>
      </Section>

      <Section title="Social Profiles">
        {["linkedin", "twitter", "facebook", "instagram", "youtube"].map((s) => (
          <F key={s} label={s[0].toUpperCase() + s.slice(1)}><Input value={social[s] || ""} onChange={(e) => setNested("social", s, e.target.value)} /></F>
        ))}
      </Section>

      <Section title="Disclosures">
        <F label="Risk Warning" full><Textarea rows={4} value={form.risk_warning || ""} onChange={(e) => set("risk_warning", e.target.value)} data-testid="settings-risk" /></F>
        <F label="IB Disclosure" full><Textarea rows={5} value={form.ib_disclosure || ""} onChange={(e) => set("ib_disclosure", e.target.value)} data-testid="settings-ib" /></F>
      </Section>

      <Section title="Trust Metrics (shown on homepage when set)">
        <div className="sm:col-span-2 space-y-2">
          {metrics.map((m, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="Value (e.g. 12+)" value={m.value} onChange={(e) => { const c = [...metrics]; c[i] = { ...c[i], value: e.target.value }; set("trust_metrics", c); }} />
              <Input placeholder="Label (e.g. Broker Partners)" value={m.label} onChange={(e) => { const c = [...metrics]; c[i] = { ...c[i], label: e.target.value }; set("trust_metrics", c); }} />
              <Button variant="ghost" size="icon" onClick={() => set("trust_metrics", metrics.filter((_, x) => x !== i))}><Trash2 className="h-4 w-4 text-danger" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => set("trust_metrics", [...metrics, { value: "", label: "" }])} data-testid="settings-add-metric"><Plus className="h-4 w-4" /> Add metric</Button>
        </div>
      </Section>

      <Section title="SEO Defaults">
        <F label="Default Title" full><Input value={seo.default_title || ""} onChange={(e) => setNested("seo", "default_title", e.target.value)} /></F>
        <F label="Default Description" full><Textarea rows={2} value={seo.default_description || ""} onChange={(e) => setNested("seo", "default_description", e.target.value)} /></F>
      </Section>

      <Section title="Analytics IDs">
        {[["gtm_id", "Google Tag Manager"], ["ga4_id", "GA4"], ["google_ads_id", "Google Ads"], ["meta_pixel_id", "Meta Pixel"], ["linkedin_id", "LinkedIn Insight"]].map(([k, l]) => (
          <F key={k} label={l}><Input value={analytics[k] || ""} onChange={(e) => setNested("analytics", k, e.target.value)} /></F>
        ))}
      </Section>

      <div className="sticky bottom-0 mt-8 border-t border-hair bg-canvas py-4">
        <Button onClick={() => save.mutate()} disabled={save.isPending} className="bg-finblue hover:bg-finblueDark" data-testid="settings-save">{save.isPending ? "Saving..." : "Save Settings"}</Button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8">
      <h2 className="mb-4 font-heading text-lg font-semibold text-ink">{title}</h2>
      <div className="grid gap-4 rounded-xl border border-hair bg-white p-6 shadow-sm sm:grid-cols-2">{children}</div>
    </div>
  );
}
function F({ label, full, children }) {
  return <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}><Label className="text-sm">{label}</Label>{children}</div>;
}
